import { useLocalization } from '@magicdoorinc/ui';
import { isEmptyData } from '~/utils/tool';
import {
  getCityValidationError,
  getStateValidationError,
  getStreetAddress1ValidationError,
  getZipCodeValidationError,
  getCountryValidationError,
} from '~/utils/validations';
import type { BetterForm } from '@magicdoorinc/form';
import type { AddressDto } from '~/swagger/portal/Api';

export function validateAddressFormat(address: AddressDto, isRequired: boolean = true): boolean | string {
  if (!address) {
    return true;
  }

  const validationErrors: Partial<AddressDto> = {
    streetAddress1: getStreetAddress1ValidationError(address.streetAddress1, isRequired),
    city: getCityValidationError(address.city, isRequired),
    state: getStateValidationError(address.state, isRequired),
    zipCode: getZipCodeValidationError(address.zipCode, isRequired),
    country: getCountryValidationError(address.country, isRequired),
  };

  if (isEmptyData(validationErrors)) {
    return true;
  }

  return JSON.stringify(validationErrors);
}

type getRules = (t: (str: string, options?: Record<string, string>) => string, ...opts: any[]) => BetterForm.Rule[];

export const email: getRules = (t) => [
  { message: t('Please enter email'), required: true },
  { message: t('Email is not valid'), type: 'email' },
];

export const phone: getRules = (t) => [
  { message: t('Please enter phone'), required: true },
  { type: 'phone', message: t('Phone is not valid') },
];

export const ssn: getRules = (t) => [
  { required: true, message: t('Please enter social security number') },
  { type: 'ssn', message: t('SSN is not valid') },
];

export const ssnPresence: getRules = (t) => [{ required: true, message: t('Please enter social security number') }];

export const ein: getRules = (t) => [
  { message: t('Please enter EIN'), required: true },
  { type: 'ein', message: t('EIN is not valid') },
];

export const einPresence: getRules = (t) => [{ required: true, message: t('Please enter EIN') }];

export const emailPresence: getRules = (t) => [{ required: true, message: t('Please enter email') }];

export const phonePresence: getRules = (t) => [{ required: true, message: t('Please enter phone') }];

export const bankAccountNumber: getRules = (t, isCompanyAccount = false) => {
  const rules: BetterForm.Rule[] = [{ required: !isCompanyAccount, message: t('Please enter account number') }];

  if (!isCompanyAccount) {
    rules.push({ regex: /^\d{4,17}$/, message: t('Account number must be between 4 and 17 digits') });
  } else {
    rules.push({
      validator: (value: string) => {
        if (!value) return true;
        return /^\d{4,17}$/.test(value);
      },
      message: t('Account number must be between 4 and 17 digits'),
    });
  }

  return rules;
};

export const bankAccountRoutingNumber: getRules = (t, isCompanyAccount = false) => {
  const rules: BetterForm.Rule[] = [{ required: !isCompanyAccount, message: t('Please enter routing number') }];

  rules.push({
    validator: (value: string) => {
      if (!value) return true;
      return /^\d{9}$/.test(value);
    },
    message: t('Invalid routing number'),
  });

  return rules;
};

type getError = (value: string, t: Translate) => string | false;

export const bankAccountType: getRules = (t, isCompanyAccount = false) => [
  { required: !isCompanyAccount, message: t('Please select an account type') },
];

export const getPropertyAddress1ValidError: getError = (address1: string, t: Translate): string | false => {
  if (!address1) {
    return t('Street address 1 is required');
  } else if (
    address1.length > 50 ||
    /^[A-Za-z0-9\s.,'#-]+(?: [A-Za-z0-9\s.,'#-]+)*,\s*[A-Za-z ]+(?:\s[A-Za-z ]+)*,\s*[A-Z]{2}(?:\s\d{5})?$/.test(address1)
  ) {
    return t('Street address 1 is not valid');
  }
  return false;
};

export const getRentalApplicationAddress1ValidError: getError = (address1: string, t: Translate): string | false => {
  if (!address1) {
    return t('Street address 1 is required');
  } else if (address1.length > 50 || !/^(?!.*([&][#]))[A-Za-z0-9 #(),&.,'_+~/*-]*$/.test(address1)) {
    return t('Street address 1 is not valid');
  }
  return false;
};

export const getPropertyAddress2ValidError: getError = (address2: string, t: Translate): string | false => {
  if (
    address2 &&
    (/^[A-Za-z0-9\s.,'#-]+(?: [A-Za-z0-9\s.,'#-]+)*,\s*[A-Za-z ]+(?:\s[A-Za-z ]+)*,\s*[A-Z]{2}(?:\s\d{5})?$/.test(address2) ||
      address2.length > 100)
  ) {
    return t('Street address 2 is not valid');
  }
  return false;
};

export const getRentalApplicationAddress2ValidError: getError = (address2: string, t: Translate): string | false => {
  if (address2 && (!/^(?!.*([&][#]))[A-Za-z0-9 #(),&.,'_+~/*-]*$/.test(address2) || address2.length > 100)) {
    return t('Street address 2 is not valid');
  }
  return false;
};

export const getCityValidError: getError = (city: string, t: Translate): string | false => {
  if (!city) {
    return t('City is required');
  } else if (!/^[A-Za-zÀ-ÿ '-.,]{2,27}$/.test(city)) {
    return t('City is not valid');
  }
  return false;
};
export const getStateValidError: getError = (state: string, t: Translate): string | false => {
  if (!state) {
    return t('State is required');
  } else if (!/^[A-Za-z]{2}$/.test(state)) {
    return t('State must be 2 characters');
  }
  return false;
};
export const getZipCodeValidError: getError = (zipCode: string, t: Translate): string | false => {
  if (!zipCode) {
    return t('Zip code is required');
  } else if (!/^\d{5}$/.test(zipCode)) {
    return t('Zip code must be 5 digits');
  }
  return false;
};

export const getCountryValidError: getError = (country: string, t: Translate): string | false => {
  if (!country || country.trim() === '') {
    return t('Country is required');
  } else if (!/^[a-zA-ZÀ-ÖØ-öø-ÿ' -]{2,50}$/.test(country)) {
    return t('Country is not valid');
  }
  return false;
};

export const addressRequired = (t: Translate, validators?: Record<string, getError>): BetterForm.Rule[] => {
  return [
    {
      validator: (value: any = {}) => {
        const errors: Record<string, string> = {};

        const combinedValidators: Record<string, getError> = {
          streetAddress1: getPropertyAddress1ValidError,
          streetAddress2: getPropertyAddress2ValidError,
          city: getCityValidError,
          state: getStateValidError,
          zipCode: getZipCodeValidError,
          country: getCountryValidError,
          ...validators,
        };

        for (const [key, validator] of Object.entries(combinedValidators)) {
          const error = validator(value[key], t);
          if (error) {
            errors[key] = error;
          }
        }

        if (Object.keys(errors).length > 0) {
          return JSON.stringify(errors);
        }
        return true;
      },
    },
  ];
};

type Translate = (s: string, params?: Record<string, string>) => string;

export const tenantName = (name: string): BetterForm.Rule[] => {
  const { t } = useLocalization();
  return [
    {
      type: 'tenantName',
      message: t('{name} must be 2-150 characters long and can only contain letters, periods, commas, spaces, or hyphens.', {
        name,
      }),
    },
  ];
};
